import { getConversationsList, postConversationClose } from './conversationsApi'
import type { ConversationAuthPair } from '../../utils/conversationUserAuthHeader'
import {
  fetchAndActivateRemoteConfig,
  getConversationCloseActionFromRemote,
} from '../../configs/firebase'

/**
 * Fase 5 — encerra a conversa do par ao **finalizar ou cancelar** a consulta.
 *
 * Neste cliente **não existe botão avulso de encerrar**: o encerramento é
 * consequência de o atendimento terminar — por conclusão ou por cancelamento.
 * O profissional já entende esses dois gestos, e o modal que confirma cada um
 * já é a confirmação que o contrato exige (encerrar remove a escrita dos dois
 * lados). O `reason` distingue os casos no log de auditoria.
 *
 * **Nunca lança.** Quando isto roda, o atendimento já foi finalizado (com o
 * prontuário salvo) ou já foi cancelado; se o encerramento falhar, o chat
 * apenas continua aberto.
 * Perder a finalização ou o cancelamento porque o chat não encerrou seria
 * inaceitável — o dado clínico vale mais que o estado da conversa.
 */
export async function encerrarConversaDoPar(
  personId: string | undefined,
  professionalId: string | undefined,
  /**
   * Rótulo de lista fixa. **Vai para log de auditoria**: proibido sintoma,
   * diagnóstico, medicação, nome, telefone ou qualquer dado pessoal.
   */
  reason:
    | 'atendimento_concluido'
    | 'atendimento_cancelado' = 'atendimento_concluido'
): Promise<'encerrada' | 'desligada' | 'sem-conversa' | 'falhou'> {
  /**
   * Garante o fetch **antes** de ler a flag.
   *
   * O getter é síncrono e devolve o que estiver ativado no momento — com o
   * default `false` até o `fetchAndActivate` completar. Como esta função roda
   * fora de qualquer hook (é chamada do fluxo de finalizar/cancelar), ela não
   * podia contar com o fetch que os hooks do chat fazem: se o profissional
   * finalizasse sem ter aberto a aba de chat, a flag valia `false` e o
   * encerramento era descartado em silêncio.
   *
   * A promessa é memoizada no `firebase.ts`, então isto não gera fetch extra.
   */
  await fetchAndActivateRemoteConfig().catch(() => undefined)

  // `conversation_close_action` (Passos 5–6) é o que escreve: sem ela ligada,
  // finalizar/cancelar não toca na conversa.
  if (!getConversationCloseActionFromRemote()) return 'desligada'

  const person = (personId ?? '').trim()
  const professional = (professionalId ?? '').trim()
  if (!person || !professional) return 'sem-conversa'

  const pair: ConversationAuthPair = {
    personId: person,
    professionalId: professional,
  }

  try {
    // O `close` usa o **UUID interno** da conversa, não o `CH…` do Twilio.
    const existentes = await getConversationsList(pair, {
      person_id: person,
      professional_id: professional,
    })

    /**
     * Tolerante à forma da resposta.
     *
     * O contrato documenta camelCase (`personId`), mas basta um ambiente
     * devolver snake_case para o `find` falhar em silêncio — e o sintoma seria
     * exatamente este: a listagem sai, o `close` não, e nada aparece na tela.
     */
    const lista = Array.isArray(existentes) ? existentes : []
    const mesmoPar = (c: Record<string, unknown>) => {
      const p1 = String(c.personId ?? c.person_id ?? '')
      const p2 = String(c.professionalId ?? c.professional_id ?? '')
      return p1 === person && p2 === professional
    }

    const conversa =
      (lista as unknown as Record<string, unknown>[]).find(mesmoPar) ??
      // Par único por conversa: se a API já filtrou pela query, a única da
      // lista é a que queremos.
      (lista.length === 1
        ? (lista[0] as unknown as Record<string, unknown>)
        : undefined)

    const conversationId = conversa?.id ?? conversa?.conversationId

    // Par sem conversa é caso legítimo: consulta cujo chat nunca foi aberto
    // não tem o que encerrar.
    if (!conversationId) return 'sem-conversa'

    await postConversationClose(String(conversationId), pair, {
      closedBy: 'professional',
      reason,
    })

    return 'encerrada'
  } catch (e) {
    // Silencioso de propósito: ver docstring. Log para diagnóstico, sem alerta.
    console.warn('[Chat] não foi possível encerrar a conversa', e)
    return 'falhou'
  }
}
